import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { AppDispatch, RootState } from '../../../store/store';
import { getStudents, StudentListItem } from '../api/students';
import { getRecordingConsent, RecordingConsentNotice } from '../api/enrollments';
import { getParentLearnerRequest, ParentLearnerRequest } from '../api/learner-requests';
import {
  clearCyclePreview,
  clearEnrollmentError,
  clearSelectedEnrollment,
  fetchCyclePreview,
  startCheckout,
} from '../store/enrollmentsSlice';
import { fetchCourseDetail, clearSelectedCourse } from '../store/coursesSlice';
import { formatMoney } from '../../../commodity/format/money';
import { closingLabel } from '../vocabulary/learningVocabulary';

/**
 * Who is being enrolled, before the parent touches the control (PRT-BR-P07-03,
 * AD-P07-01). The learner the parent is acting as, if they are acting as one;
 * the only learner, if there is just one; otherwise nobody, and the parent
 * chooses. It used to be whichever learner the API listed first, so a parent
 * browsing as their second child arrived here with the first one selected and
 * a price quoted for them — the prototype's "enrollment resets the learner"
 * defect (PG-P02-05), reproduced one screen later.
 */
export const initialLearner = (
  students: Pick<StudentListItem, 'studentId'>[],
  activeStudentId?: string,
): string => {
  if (activeStudentId && students.some((s) => s.studentId === activeStudentId)) {
    return activeStudentId;
  }
  return students.length === 1 ? students[0].studentId : '';
};

const EnrollmentConfirm: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  // DEC-S2-S06-06 — arriving from a learner's request pins the learner to it.
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('request');
  const [request, setRequest] = useState<ParentLearnerRequest | null>(null);
  const [requestError, setRequestError] = useState(false);

  const { selectedCourse } = useSelector((state: RootState) => state.courses);
  // DEC-S2-S04-01: the offer the parent read, handed over by the course page. A
  // direct link has none, and takes the one this screen is showing. A request
  // names its own, and that is the one being answered (DEC-S2-S06-06). The server
  // has the last word either way (OFFER_CHANGED).
  const offerId = request?.offerId ?? searchParams.get('offer') ?? selectedCourse?.offer?.offerId;
  const activeStudentId = useSelector((state: RootState) => state.context.activeContext?.studentId);
  const { cyclePreview, submitting, error, conflictEnrollmentId } = useSelector(
    (state: RootState) => state.enrollments,
  );

  const [students, setStudents] = useState<StudentListItem[]>([]);
  const [chosenChildId, setChildId] = useState<string>('');
  const childId = requestId ? request?.learnerId ?? '' : chosenChildId;
  // What the request named has moved on: the Parent decides again from the
  // course as it is, and this screen does not carry on as though nothing changed.
  const requestStands =
    !requestId || (request?.decidable === true && request.courseId === courseId);
  const [studentsLoading, setStudentsLoading] = useState(true);
  // MVP-01: every class is recorded; the guardian accepts it here, once, at
  // enrollment. Playback is a separate grant and starts unticked.
  const [notice, setNotice] = useState<RecordingConsentNotice | null>(null);
  const [noticeError, setNoticeError] = useState(false);
  const [captureAccepted, setCaptureAccepted] = useState(false);
  const [playbackGranted, setPlaybackGranted] = useState(false);

  const loadNotice = async (): Promise<RecordingConsentNotice | null> => {
    try {
      const next = await getRecordingConsent();
      setNotice(next);
      setNoticeError(false);
      return next;
    } catch {
      setNoticeError(true);
      return null;
    }
  };

  const loadRequest = async () => {
    if (!requestId) return;
    try {
      setRequest(await getParentLearnerRequest(requestId));
      setRequestError(false);
    } catch {
      setRequestError(true);
    }
  };

  useEffect(() => {
    loadRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  useEffect(() => {
    let cancelled = false;
    getRecordingConsent()
      .then((next) => {
        if (!cancelled) setNotice(next);
      })
      .catch(() => {
        if (!cancelled) setNoticeError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (courseId) {
      dispatch(fetchCourseDetail(courseId));
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await getStudents();
        if (!cancelled) {
          setStudents(list);
          setChildId(initialLearner(list, activeStudentId));
        }
      } catch {
        if (!cancelled) {
          setStudents([]);
        }
      } finally {
        if (!cancelled) {
          setStudentsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      dispatch(clearCyclePreview());
      dispatch(clearSelectedEnrollment());
      dispatch(clearSelectedCourse());
    };
    // PRT-BR-P02-11 / AC-15 — the learner is read once, when the screen opens;
    // switching context mid-checkout must not silently change who is being
    // enrolled. Only the parent's own choice in the select below changes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, courseId]);

  useEffect(() => {
    if (courseId && childId) {
      dispatch(fetchCyclePreview({ courseId, childId }));
    }
  }, [dispatch, courseId, childId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!courseId || !offerId || !childId || !notice || !captureAccepted || !requestStands) return;
    dispatch(clearEnrollmentError());
    // Send the total the parent is looking at. If the course was re-priced
    // since, the server refuses rather than charge a number nobody confirmed.
    const result = await dispatch(
      startCheckout({
        courseId,
        childId,
        offerId,
        expectedTotal: cyclePreview?.total,
        recordingCaptureConsent: captureAccepted,
        recordingPlaybackConsent: playbackGranted,
        recordingConsentVersion: notice.version,
        ...(request
          ? {
              learnerRequest: { requestId: request.requestId, expectedVersion: request.version },
            }
          : {}),
      }),
    );
    if (startCheckout.fulfilled.match(result)) {
      // Redirect the parent to Stripe's hosted Checkout page.
      window.location.assign(result.payload.checkoutUrl);
      return;
    }
    await loadRequest();
    // If the notice changed under the parent, show the new words and ask
    // again: agreeing to text they did not read is not consent.
    const latest = await loadNotice();
    if (latest && latest.version !== notice.version) {
      setCaptureAccepted(false);
      setPlaybackGranted(false);
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <button
        onClick={() => navigate(courseId ? `/courses/${courseId}` : '/courses')}
        style={{
          background: 'none',
          border: 'none',
          color: '#0366d6',
          cursor: 'pointer',
          marginBottom: 16,
          fontSize: 14,
        }}
      >
        &larr; Volver al curso
      </button>

      <h1 style={{ marginBottom: 8 }}>Confirmar inscripcion</h1>
      {selectedCourse && (
        <p style={{ color: '#555', marginTop: 0 }}>
          Curso: <strong>{selectedCourse.title}</strong>
        </p>
      )}

      {requestId && requestError && (
        <p role="alert" style={{ color: '#c92a2a' }}>
          No pudimos cargar la solicitud de tu hijo. Inténtalo de nuevo.
        </p>
      )}
      {request && !requestStands && (
        <p role="alert" style={{ color: '#c92a2a' }}>
          Lo que se pidió ya no está disponible así. No lo cambiamos por otro horario: revisa el
          curso como está ahora y decide de nuevo.
        </p>
      )}

      <form onSubmit={handleSubmit} style={{ marginTop: 24 }}>
        <section style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Alumno</h3>
          {studentsLoading && <p>Cargando alumnos...</p>}
          {!studentsLoading && students.length === 0 && (
            <p>
              No tienes alumnos registrados. <a href="/profile">Agrega un alumno desde tu perfil</a>
              .
            </p>
          )}
          {!studentsLoading && students.length > 0 && (
            <select
              aria-label="Alumno que se inscribe"
              disabled={Boolean(requestId)}
              value={childId}
              onChange={(e) => setChildId(e.target.value)}
              style={inputStyle}
              required
            >
              {childId === '' && (
                <option value="" disabled>
                  Elige a quién inscribes
                </option>
              )}
              {students.map((s) => (
                <option key={s.studentId} value={s.studentId}>
                  {s.firstName} {s.lastName} (Grado {s.grade})
                </option>
              ))}
            </select>
          )}
        </section>

        {cyclePreview && (
          <section style={cardStyle}>
            <h3 style={{ marginTop: 0 }}>Resumen del ciclo</h3>
            <p style={{ margin: '4px 0' }}>
              <strong>
                {cyclePreview.sessionCount} sesiones + {closingLabel().toLowerCase()}
              </strong>
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
              <tbody>
                <Row
                  label="25% al activar (booking fee)"
                  value={cyclePreview.escrow.bookingFee}
                  currency={selectedCourse?.currency}
                />
                <Row
                  label={`50% por sesiones (${cyclePreview.sessionCount} x ${formatMoney(
                    cyclePreview.escrow.perSession,
                    selectedCourse?.currency,
                  )})`}
                  value={cyclePreview.escrow.perSession * cyclePreview.sessionCount}
                  currency={selectedCourse?.currency}
                />
                <Row
                  label="25% al cierre (con retención)"
                  value={cyclePreview.escrow.holdback}
                  currency={selectedCourse?.currency}
                />
                <Row
                  label="Total"
                  value={cyclePreview.total}
                  currency={selectedCourse?.currency}
                  bold
                />
              </tbody>
            </table>
          </section>
        )}

        <section style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Metodo de pago</h3>
          <p style={{ color: '#555', fontSize: 13, marginTop: 0, marginBottom: 0 }}>
            Te redirigiremos a la pagina segura de pago de Stripe para completar la inscripcion. En
            modo de prueba usa la tarjeta <code>4242 4242 4242 4242</code>.
          </p>
          {/* PRT-07-FR-04 / D-P07-03 (CTO, 12 Oct 2026): authorize now, charge on acceptance. */}
          <p style={{ color: '#1c4f80', fontSize: 13, marginBottom: 0 }}>
            Tu tarjeta solo se autoriza: no se cobra nada hasta que el profesor acepte la solicitud.
            Si no la acepta o no responde a tiempo, la autorización se libera sin cargo.
          </p>
        </section>

        <section style={cardStyle} aria-labelledby="recording-notice-title">
          <h3 id="recording-notice-title" style={{ marginTop: 0 }}>
            Grabación de las clases
          </h3>
          {!notice && !noticeError && <p>Cargando aviso de grabación...</p>}
          {noticeError && !notice && (
            <p style={{ color: '#c92a2a' }}>
              No pudimos cargar el aviso de grabación. Sin él no se puede completar la inscripción.
            </p>
          )}
          {notice && (
            <>
              <p style={{ fontSize: 14, marginTop: 0 }}>{notice.capture}</p>
              <label style={checkLabelStyle}>
                <input
                  type="checkbox"
                  checked={captureAccepted}
                  onChange={(e) => setCaptureAccepted(e.target.checked)}
                  required
                />
                <span>Acepto que todas las clases se graben (obligatorio)</span>
              </label>
              <label style={checkLabelStyle}>
                <input
                  type="checkbox"
                  checked={playbackGranted}
                  onChange={(e) => setPlaybackGranted(e.target.checked)}
                />
                <span>{notice.playback} (opcional)</span>
              </label>
            </>
          )}
        </section>

        {error && (
          <div style={errorStyle}>
            <strong>Error:</strong> {error}
            {conflictEnrollmentId && (
              <div style={{ marginTop: 8 }}>
                <a
                  href={`/enrollments/${conflictEnrollmentId}`}
                  style={{ color: '#c92a2a', textDecoration: 'underline' }}
                >
                  Ver inscripcion existente
                </a>
              </div>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={
            submitting ||
            !offerId ||
            !childId ||
            !cyclePreview ||
            students.length === 0 ||
            !notice ||
            !captureAccepted ||
            !requestStands
          }
          style={primaryButtonStyle(
            submitting ||
              !offerId ||
              !childId ||
              !cyclePreview ||
              students.length === 0 ||
              !notice ||
              !captureAccepted ||
              !requestStands,
          )}
        >
          {submitting ? 'Redirigiendo a Stripe...' : 'Continuar al pago'}
        </button>
      </form>
    </div>
  );
};

const Row: React.FC<{ label: string; value: number; currency?: string; bold?: boolean }> = ({
  label,
  value,
  currency,
  bold,
}) => (
  <tr style={{ borderTop: '1px solid #e9ecef' }}>
    <td style={{ padding: '8px 0', fontWeight: bold ? 700 : 400 }}>{label}</td>
    <td
      style={{
        padding: '8px 0',
        textAlign: 'right',
        fontWeight: bold ? 700 : 400,
      }}
    >
      {formatMoney(value, currency)}
    </td>
  </tr>
);

const cardStyle: React.CSSProperties = {
  border: '1px solid #d1d5da',
  borderRadius: 8,
  padding: 16,
  marginBottom: 16,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #ced4da',
  borderRadius: 6,
  fontSize: 14,
  boxSizing: 'border-box',
};

const checkLabelStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'flex-start',
  fontSize: 14,
  marginTop: 8,
};

const errorStyle: React.CSSProperties = {
  background: '#ffe3e3',
  border: '1px solid #ff8787',
  borderRadius: 8,
  padding: 12,
  marginBottom: 16,
  color: '#c92a2a',
};

const primaryButtonStyle = (disabled: boolean): React.CSSProperties => ({
  width: '100%',
  padding: 14,
  backgroundColor: disabled ? '#a0c4e8' : '#1971c2',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 16,
  fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

export default EnrollmentConfirm;
